import { ContentStats } from 'pages/ModulePlayer/ContentStats';
import { ModuleTypeEnum } from 'services/api';

export enum QuestionFormatEnum {
  mcq = 'mcq',
  subjective_rearrange = 'subjective_rearrange',
  one_word = 'one_word',
  arrange_the_following = 'arrange_the_following',
  sequencing = 'sequencing',
  match_the_following = 'match_the_following',
  error_analysis = 'error_analysis',
  fill_in_the_blanks = 'fill_in_the_blanks',
}

export enum HintTypeEnum {
  text = 'text',
  image = 'image',
}

export enum ExplanationTypeEnum {
  text = 'text',
  image = 'image',
  audio = 'audio',
  video = 'video',
  pdf = 'pdf',
}

export enum QuestionMediaTypeEnum {
  image = 'image',
  audio = 'audio',
}

export enum McqOptionTypeEnum {
  text = 'text',
  image = 'image',
  audio = 'audio',
}

export enum StageStatusEnum {
  COMPLETED = 'COMPLETED',
  UNLOCKED = 'UNLOCKED',
  RETRY = 'RETRY',
  LOCKED = 'LOCKED',
}

export enum EngagementStrategyStatusEnum {
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING',
}

export enum MissionStatusEnum {
  LOCKED = 'LOCKED',
  UNLOCKED = 'UNLOCKED',
  COMPLETED = 'COMPLETED',
  RETRY = 'RETRY',
}

enum McqOptionEvidenceTypeEnum {
  eom = 'eom',
  eoc = 'eoc',
}

export enum SubjectiveRearrangeOptionTypeEnum {
  text = 'text',
}

export enum ArrangeTheFollowingOptionTypeEnum {
  text = 'text',
  image = 'image',
}

export enum SequencingOptionTypeEnum {
  text = 'text',
}

export enum MatchTheFollowingOptionTypeEnum {
  text = 'text',
  image = 'image',
}

export enum ErrorAnalysisOptionTypeEnum {
  text = 'text',
  image = 'image',
}

export enum AnswerFeedbackEnum {
  no_feedback = 'no_feedback',
  status_feedback = 'status_feedback',
  full_feedback = 'full_feedback',
}

export type EomModuleTypeEnum = Extract<ModuleTypeEnum, ModuleTypeEnum.video | ModuleTypeEnum.pdf>;

export interface QuestionBase {
  id: string;
  difficulty: number;
  type: number;
  format: QuestionFormatEnum;
  mediumMapping: {
    [languageCode: string]: MediumMapping;
  };
}

export interface MediumMapping {
  questionText: string;
  questionMediaType: QuestionMediaTypeEnum | null;
  questionMedia: string | null;
  metadata: QuestionMetadata;
}

export interface Hint {
  type: HintTypeEnum;
  value: string;
}

export interface Explanation {
  explanation: string | null;
  explanationType: ExplanationTypeEnum | null;
}

export interface EomModule {
  moduleType: EomModuleTypeEnum;
  path: string;
  id: string;
}

export interface QuestionMetadata extends Explanation {
  questionFormatData:
    | McqFormat
    | SubjectiveRearrangeFormat
    | OneWordFormat
    | ArrangeTheFollowingFormat
    | SequencingFormat
    | MatchTheFollowingFormat
    | ErrorAnalysisFormat
    | FillInTheBlanksFormat;

  hints: Hint[];
}

export interface QuestionFormatData {}

export interface McqFormat extends QuestionFormatData {
  options: ({
    type: McqOptionTypeEnum;
    value: string;
    evidenceType: McqOptionEvidenceTypeEnum | null;
    evidenceId: string | null;
    isCorrect: boolean;
  } & Explanation)[];
}

export interface SubjectiveRearrangeFormat extends QuestionFormatData {
  steps: {
    type: SubjectiveRearrangeOptionTypeEnum;
    value: string;
    isMovable: boolean;
  }[];
  distractors: {
    type: SubjectiveRearrangeOptionTypeEnum;
    value: string;
  }[];
}

export interface OneWordFormat extends QuestionFormatData {
  answers: string[];
}

export interface ArrangeTheFollowingFormat extends QuestionFormatData {
  type: ArrangeTheFollowingOptionTypeEnum;
  options: string[];
}

export interface SequencingFormat extends QuestionFormatData {
  type: SequencingOptionTypeEnum;
  options: string[];
}

export interface MatchTheFollowingFormat extends QuestionFormatData {
  answerType: MatchTheFollowingOptionTypeEnum;
  questionType: MatchTheFollowingOptionTypeEnum;
  answerData: string[];
  questionData: string[];
  // explanationData: (Explanation | null)[];
}

export interface ErrorAnalysisFormat extends QuestionFormatData {
  options: {
    type: ErrorAnalysisOptionTypeEnum;
    value: string;
    isCorrect: boolean;
    correctType?: ErrorAnalysisOptionTypeEnum;
    correctValue?: string;
  }[];
}

export interface FillInTheBlanksFormat extends QuestionFormatData {
  questionText: string; // # is represented as blank in ui
  answerText: string; // answers separated by $$
}

export type QuestionType =
  | QuestionMcq
  | QuestionSubjectiveRearrange
  | QuestionOneWord
  | QuestionArrangeTheFollowing
  | QuestionSequencing
  | QuestionMatchTheFollowing
  | QuestionErrorAnalysis
  | QuestionFillInTheBlanks;

export interface QuestionMcq extends QuestionBase {
  format: QuestionFormatEnum.mcq;
}

export interface QuestionSubjectiveRearrange extends QuestionBase {
  format: QuestionFormatEnum.subjective_rearrange;
}

export interface QuestionOneWord extends QuestionBase {
  format: QuestionFormatEnum.one_word;
}

export interface QuestionArrangeTheFollowing extends QuestionBase {
  format: QuestionFormatEnum.arrange_the_following;
}

export interface QuestionSequencing extends QuestionBase {
  format: QuestionFormatEnum.sequencing;
}

export interface QuestionMatchTheFollowing extends QuestionBase {
  format: QuestionFormatEnum.match_the_following;
}

export interface QuestionErrorAnalysis extends QuestionBase {
  format: QuestionFormatEnum.error_analysis;
}

export interface QuestionFillInTheBlanks extends QuestionBase {
  format: QuestionFormatEnum.fill_in_the_blanks;
}

export type AnswerType =
  | McqAnswerFormat
  | SubjectiveRearrangeAnswerFormat
  | OneWordAnswerformat
  | ArrangeTheFollowingAnswerFormat
  | SequencingAnswerFormat
  | MatchTheFollowingAnswerFormat
  | ErrorAnalysisAnswerFormat
  | FillInTheBlanksAnswerFormat;

export type AnswerStatus = 'correct' | 'incorrect';
export type AttemptData = {
  answer?: AnswerType;
  status?: AnswerStatus;
  submitted: boolean;
  questionId?: string;
};

export interface McqAnswerFormat {
  type: McqOptionTypeEnum;
  userAnswer: string; // as in primary medium
}

export interface SubjectiveRearrangeAnswerFormat {
  userAnswer: {
    type: SubjectiveRearrangeOptionTypeEnum;
    value: string; // as in primary medium
  }[];
}

export interface OneWordAnswerformat {
  userAnswer: string;
}

export interface ArrangeTheFollowingAnswerFormat {
  type: ArrangeTheFollowingOptionTypeEnum;
  userAnswer: string[]; // as in primary medium
}

export interface SequencingAnswerFormat {
  type: SequencingOptionTypeEnum;
  userAnswer: string[]; // as in primary medium
}

export interface MatchTheFollowingAnswerFormat {
  answerType: MatchTheFollowingOptionTypeEnum;
  userAnswer: string[]; // as in primary medium
}

export interface ErrorAnalysisAnswerFormat {
  userAnswer: (boolean | undefined)[];
}

export interface FillInTheBlanksAnswerFormat {
  userAnswer: string[];
}

export interface AttemptInfo {
  answer: AnswerType;
  startTime: string;
  endTime: string;
  duration: number;
}

export interface EomModuleStats {
  questionId: string;
  eomModuleId: string;
  attemptId: string;
  moduleType: string;
  metadata: ContentStats;
}
